import { useMutation, useQuery } from '@apollo/client/react';
import { ScrollPage } from '@optools/components/layout/layout';
import { PageLoader } from '@optools/components/loading/page-loader';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { useAccessCheck } from '@optools/hooks/useAccessCheck';
import { UPDATE_CDN_STATUS } from '@optools/services/operator-gateway/btc/mutations';
import { GET_PLAYBACK_CDNS } from '@optools/services/operator-gateway/btc/queries';
import { Cdn, CdnStatus } from '@optools/services/operator-gateway/types/generated/graphql';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { CdnCard } from '@optools/tools/btc-tools/components/cdn-card';
import { authorizedRouteLoader } from '@optools/utils/auth/authorized-route-loader';
import { createFileRoute } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_auth/btc-tools/cdn-status')({
  beforeLoad: authorizedRouteLoader('btcTools.cdnStatus'),
  component: RouteComponent,
});

export function RouteComponent() {
  const [open, setOpen] = useState(false);
  const [selectedCdn, setSelectedCdn] = useState<CdnStatus | undefined>(undefined);
  const { checkGroupAccess } = useAccessCheck();
  const config = useAppSelector(selectToolsConfig).btcTools;
  const { cdnMapping } = config.pages.cdnStatus;
  const { cdnFailover } = config.auth.pages.cdnStatus.featureGroups;
  const { data, loading } = useQuery(GET_PLAYBACK_CDNS);
  const [updateCdnStatus, { loading: updateCdnStatusLoading }] = useMutation(UPDATE_CDN_STATUS, {
    awaitRefetchQueries: true,
    onCompleted: ({ updateCdnStatus }) => {
      if (!updateCdnStatus) {
        toast.error('Update CDN Status', {
          description: 'Failed to update CDN status',
          duration: Infinity,
        });
        return;
      }

      setOpen(false);
      toast.success('Update CDN Status', {
        description: `Updated the CDN status successfully`,
      });
    },
    refetchQueries: [{ query: GET_PLAYBACK_CDNS }],
  });

  const canDisableCdns =
    data?.playbackCdns && data.playbackCdns?.filter((item) => item.enabled)?.length >= 2;

  const handleUpdateCdnStatus = () => {
    if (selectedCdn) {
      updateCdnStatus({ variables: { cdn: selectedCdn.cdn, enabled: !selectedCdn.enabled } });
    }
  };
  const canUserAccess = checkGroupAccess(cdnFailover);

  const playbackCdns = data?.playbackCdns
    ? [...data.playbackCdns]?.sort((a, b) => {
        if (a.cdn === Cdn.MEDIA_CDN && b.cdn === Cdn.FASTLY) return 1;
        if (a.cdn === Cdn.FASTLY && b.cdn === Cdn.MEDIA_CDN) return -1;
        return 0;
      })
    : [];

  const handleCdnClick = (selectedItem: CdnStatus) => {
    if (
      selectedItem &&
      data?.playbackCdns.some((item) => item.enabled && item.cdn !== selectedItem.cdn)
    ) {
      setSelectedCdn(selectedItem);
      setOpen(true);
    }
  };

  if (loading && data?.playbackCdns?.length === 0) {
    return <PageLoader />;
  }

  return (
    <ScrollPage>
      <div className="flex flex-col items-center space-y-24">
        <h2 className="text-2xl font-bold">CDN Status</h2>
        {!canUserAccess && (
          <h4 className="text-lg">This page is readonly due to user permissions</h4>
        )}
        <div className="flex gap-4">
          {playbackCdns?.map((item) => {
            return (
              <CdnCard
                key={item.cdn}
                canDisableCdns={canDisableCdns ?? false}
                cdnStatus={item}
                loading={loading || updateCdnStatusLoading}
                readOnly={!canUserAccess}
                onClick={handleCdnClick}
              />
            );
          })}
        </div>
      </div>
      <AlertDialog
        open={open}
        onOpenChange={(open) => setOpen(open || updateCdnStatusLoading || loading)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <div className="flex items-center space-x-2">
                <TriangleAlert className="size-4 stroke-amber-400" />
                <p>{`${!selectedCdn?.enabled ? 'Enable' : 'Disable'} ${selectedCdn?.cdn ? cdnMapping[selectedCdn.cdn] : ''}`}</p>
                <TriangleAlert className="size-4 stroke-amber-400" />
              </div>
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription asChild>
            <div className="flex-col">
              <p>{`Are you sure you want to ${!selectedCdn?.enabled ? 'Enable' : 'Disable'} ${selectedCdn?.cdn ? cdnMapping[selectedCdn.cdn] : ''}?`}</p>
              <div className="flex gap-1">
                <p>{`This action will ${!selectedCdn?.enabled ? 'Enable' : 'Disable'} the CDN for`}</p>
                <p>
                  <span className="font-bold">ALL</span> Streams
                </p>
              </div>
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateCdnStatusLoading || loading}>
              Cancel
            </AlertDialogCancel>
            <Button
              disabled={loading || updateCdnStatusLoading}
              loading={loading || updateCdnStatusLoading}
              onClick={() => handleUpdateCdnStatus()}>
              Update
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScrollPage>
  );
}
